import { getConfigLayerOptions } from "../data/config-layer-options";
import { apiCall } from '../helpers/api.helper.js';
import { editPartialLayer as apiEditPartialLayer,
  readLayer as apiReadLayer,
  getRatios as apiGetRatios,
  addRatio as apiAddRatio,
  updateRatio as apiUpdateRatio,
  deleteRatio as apiDeleteRatio,
  importExtraAttributes as apiImportExtraAttributes,
  getExtraAttributesSummary as apiGetExtraAttributesSummary,
  getExtraAttributesList as apiGetExtraAttributesList,
  storeExtraAttributesDocument as apiStoreExtraAttributesDocument,
  getExtraAttributesDocument as apiGetExtraAttributesDocument } from '../api/layers.api.js';

function buildHierarchyWithChildren(statuses) {
  const map = {};
  const roots = [];

  statuses.forEach(status => {
    map[status.id] = {
      id: status.id ?? null,
      parent_id: status.parent_id ?? null,
      code: status.code ?? null,
      name: status.name ?? null,
      weight: status.weight ?? 0,
      color: status.color ?? null,
      type: status.type ?? 'group',
      children: [] // cambia de `statuses` a `children`
    };
  });

  statuses.forEach(status => {
    const node = map[status.id];
    if (node.parent_id && map[node.parent_id]) {
      map[node.parent_id].children.push(node); // asigna como hijo
    } else {
      roots.push(node);
    }
  });

  return roots;
}

/**
 * Helper para mapear un status con sus permisos transformados
 * @param {Object} s - Status del backend
 * @returns {Object} Status mapeado para el frontend
 */
function _mapStatusWithPermissions(s) {
  const permissions = (s.permissions || []).map(p => {
    if (!p || !p.permittable_type) return null;

    const permittableType = p.permittable_type.toLowerCase();
    if (permittableType.includes('user')) {
      return {
        email: p.basic_info?.email ?? null,
        fullname: p.basic_info?.fullname ?? null,
        user_id: p.basic_info?.user_id ?? null,
      }
    } else if (permittableType.includes('role')) {
      return {
        name: p.basic_info ?? null,
      }
    }
    return null;
  }).filter(Boolean);

  return {
    id: s.id ?? null,
    group_id: s.parent_id ?? s.group_id ?? null,
    code: s.code ?? null,
    name: s.name ?? null,
    weight: s.weight ?? null,
    is_completion: s.is_completion ?? false,
    color: s.color ?? null,
    type: s.type ?? 'status',
    permissions,
  }
}

/**
 * Mapea UNA sola capa del backend al formato del frontend
 * @param {Object} l - Layer del backend
 * @returns {Object} Layer mapeado para el frontend
 */
export function mapLayerLegacy(l) {
  const metadataConfig = l.metadata?.config?.statuses || {};

  const config = {
    avance_method: metadataConfig.avance_method || 'default',
    weighting_criteria: metadataConfig.weighting_criteria || 'default',
    visibility: {
      progress: metadataConfig.visibility?.progress ?? 'visible_default',
      incidents: metadataConfig.visibility?.incidents ?? 'visible_default',
      progress_roles: metadataConfig.visibility?.progress_roles ?? null,
      incidents_roles: metadataConfig.visibility?.incidents_roles ?? null,
    },
    select_multiple: {
      progress: metadataConfig.select_multiple?.progress ?? 'single',
      incidents: metadataConfig.select_multiple?.incidents ?? 'single',
    },
  };

  let groups = l.group_statuses || [];
  let ungroupedStates = (l.statuses || []).filter(s => s.type === 'status' && s.parent_id === null);
  // Mapear incidents con permisos (igual que los statuses)
  const incidents = (l.incident_statuses || []).map(s => _mapStatusWithPermissions(s));
  let statuses = [];

  if (config.avance_method === 'grouped') {
    statuses = groups.map(g => {
      let statusesGroup = (l.statuses || []).filter(s => s.parent_id === g.id);

      return {
        id: g.id ?? null,
        parent_id: g.parent_id ?? null,
        code: g.code ?? null,
        name: g.name ?? null,
        weight: g.weight ?? null,
        color: g.color ?? null,
        type: g.type ?? 'group',
        statuses: statusesGroup.map(s => _mapStatusWithPermissions(s))
      }
    })
  } else {
    statuses = ungroupedStates.map(s => _mapStatusWithPermissions(s));
    ungroupedStates = [];
  }

  const result = {
    id: l.id,
    code: l.code,
    name: l.name,
    icon: l.icon,
    weight: l.weight,
    order_column: l.order_column ?? 0,
    scheduled_start_date: l.scheduled_start_date,
    scheduled_end_date: l.scheduled_end_date,
    expanded: false,
    groups,
    statuses,
    ungrouped: ungroupedStates.map(s => _mapStatusWithPermissions(s)),
    incidents,
    config: {
      statuses: config
    }
  };

  // console.log('[mapLayer] Layer mapeado:', result);
  return result;
}

/**
 * Mapea un array de capas del backend al formato del frontend
 * @param {Array} layers - Array de layers del backend
 * @returns {Array} Array de layers mapeados para el frontend
 */
export function mapLayersLegacy(layers) {
  return layers.map(l => mapLayerLegacy(l));
}

/**
   * Mapea una capa al shape estable (sin polimorfismo):
   *   - groups:    SIEMPRE array de grupos enriquecidos con .statuses interno
   *   - statuses:  SIEMPRE array plano de todos los type='status' (con permissions resueltos)
   *   - ungrouped: SIEMPRE statuses con parent_id === null
   *   - incidents: SIEMPRE statuses tipo incident con permissions resueltos
   * Los consumidores deciden qué leer según el modo de UI (avance_method).
   *
   * Reemplaza progresivamente a mapLayerLegacy — los consumidores nuevos
   * (manager refactorizado) usan esta versión. Los legacy mantienen mapLayerLegacy.
   */
export function mapLayer(l) {
  const metadataConfig = l.metadata?.config?.statuses || {};
  const config = {
    avance_method:      metadataConfig.avance_method      || 'default',
    weighting_criteria: metadataConfig.weighting_criteria || 'default',
    visibility: {
      progress:        metadataConfig.visibility?.progress        ?? 'visible_default',
      incidents:       metadataConfig.visibility?.incidents       ?? 'visible_default',
      progress_roles:  metadataConfig.visibility?.progress_roles  ?? null,
      incidents_roles: metadataConfig.visibility?.incidents_roles ?? null,
    },
    select_multiple: {
      progress:  metadataConfig.select_multiple?.progress  ?? 'single',
      incidents: metadataConfig.select_multiple?.incidents ?? 'single',
    },
  };

  // Todos los statuses (type='status') mapeados con permisos
  const statuses = (l.statuses || [])
    .filter(s => s.type === 'status')
    .map(_mapStatusWithPermissions);

  // Grupos enriquecidos: cada grupo trae sus statuses anidados
  const groups = (l.group_statuses || []).map(g => ({
    id:        g.id ?? null,
    parent_id: g.parent_id ?? null,
    code:      g.code ?? null,
    name:      g.name ?? null,
    weight:    g.weight ?? null,
    color:     g.color ?? null,
    type:      g.type ?? 'group',
    statuses:  statuses.filter(s => s.group_id === g.id),
  }));

  // Ungrouped: subset de statuses sin parent
  const ungrouped = statuses.filter(s => s.group_id === null);

  // Incidents: type='incident' mapeados
  const incidents = (l.incident_statuses || []).map(_mapStatusWithPermissions);

  return {
    id:                   l.id,
    code:                 l.code,
    name:                 l.name,
    icon:                 l.icon,
    weight:               l.weight,
    order_column:         l.order_column ?? 0,
    scheduled_start_date: l.scheduled_start_date,
    scheduled_end_date:   l.scheduled_end_date,
    expanded:             false,
    groups,      // SIEMPRE array de grupos con sus .statuses internos
    statuses,    // SIEMPRE array plano de todos los statuses
    ungrouped,   // SIEMPRE subset de statuses sin parent_id
    incidents,
    config: { statuses: config },
  };
}

export function mapLayers(layers) {
  return layers.map(mapLayer);
}

export function getListItemsNavigation(layers, pictogramType = null) {
  return layers.map(l => {
    return {
      id: l.id,
      code: l.code,
      name: l.name,
      icon: l.icon,
      weight: l.weight,
      type: 'item',
      description: null,
      order_column: l.order_column ?? 0,
      scheduled_start_date: l.scheduled_start_date,
      scheduled_end_date: l.scheduled_end_date,
      children: getConfigLayerOptions({
        id: l.id,
        code: l.code,
        name: l.name,
        icon: l.icon,
        weight: l.weight,
        order_column: l.order_column,
        scheduled_start_date: l.scheduled_start_date,
        scheduled_end_date: l.scheduled_end_date,
        config: l.config || {},
      }, pictogramType)
    }
  })
}

export const editPartialLayer = (layerId, payload) => apiCall(apiEditPartialLayer, layerId, payload);
export const readLayer = (layerId, params) => apiCall(apiReadLayer, layerId, params);
export const getRatios = (layerId) => apiCall(apiGetRatios, layerId);
export const addRatio = (layerId, payload) => apiCall(apiAddRatio, layerId, payload);
export const updateRatio = (layerId, ratioId, payload) => apiCall(apiUpdateRatio, layerId, ratioId, payload);
export const deleteRatio = (layerId, ratioId) => apiCall(apiDeleteRatio, layerId, ratioId);
export const importExtraAttributes = (layerId, payload) => apiCall(apiImportExtraAttributes, layerId, payload);
export const getExtraAttributesSummary = (layerId) => apiCall(apiGetExtraAttributesSummary, layerId);
export const getExtraAttributesList = (layerId, params) => apiCall(apiGetExtraAttributesList, layerId, params);
export const storeExtraAttributesDocument = (layerId, payload) => apiCall(apiStoreExtraAttributesDocument, layerId, payload);
export const getExtraAttributesDocument = (layerId) => apiCall(apiGetExtraAttributesDocument, layerId);
